import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Observable } from 'rxjs';
import { AppService } from '../app/app.service';
import { TimeInvoking } from '@core/utils';

export interface PushNotification {
  title: string;
}

export interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<any>;
  addListener(
    eventName: 'registration',
    listenerFunc: (token: { value: string }) => void
  ): Promise<any>;
  addListener(
    eventName: 'pushNotificationReceived',
    listenerFunc: (notification: PushNotification) => void
  ): Promise<any>;
  addListener(
    eventName: 'pushNotificationActionPerformed',
    listenerFunc: (notification: {
      actionId: string;
      notification: any;
    }) => void
  ): Promise<any>;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const PushNotifications: PushNotificationsPlugin = {
  checkPermissions: () => Promise.resolve({ receive: 'prompt' }),
  requestPermissions: () => Promise.resolve({ receive: 'granted' }),
  register: () => Promise.resolve(),
  addListener: (eventName: string, listenerFunc: (res: any) => void) =>
    Promise.resolve()
};

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  private _deviceToken: string;

  private _permissionsGranted: boolean;

  private timeInvoking = new TimeInvoking();

  get deviceToken(): string {
    return this._deviceToken;
  }

  get permissionsGranted(): boolean {
    return this._permissionsGranted;
  }

  constructor(private appService: AppService) {}

  /**
   * inzializza le notifiche push
   */
  initPushNotifications(): void {
    this.timeInvoking.invokeOnce('initPushNotifications', async () => {
      this._deviceToken = undefined;
      this._permissionsGranted = false;

      if (!this.isPushNotificationsAvailable()) {
        return;
      }

      this.addRegistrationListener(token => {
        this._deviceToken = token.value;
      });

      try {
        await this.guaranteePermissions();
        await this.register();
        this._permissionsGranted = true;
      } catch (_) {
        this._permissionsGranted = false;
      }

      this.getWhenTap().subscribe(res => this.openNotification(res));
    });
  }

  /**
   * restituisce osservabile che emette evento quando viene ricevuta una notifica push
   */
  getWhenReceive(): Observable<any> {
    const observable = new Observable<any>(observer => {
      if (!this.isPushNotificationsAvailable()) {
        observer.complete();
        return;
      }

      this.addNotificationReceivedListener(notification => {
        observer.next(notification);
      });
    });

    return observable;
  }

  /**
   * restituisce osservabile che emette evento quando viene ricevuta una notifica push
   */
  getWhenTap(): Observable<PushNotification> {
    const observable = new Observable<PushNotification>(observer => {
      if (!this.isPushNotificationsAvailable()) {
        observer.complete();
        return;
      }

      this.addNotificationActionPerformedListener(action => {
        if (action.actionId === 'tap') {
          observer.next(action.notification);
        }
      });
    });

    return observable;
  }

  /**
   * stabilisce se le notifiche push sono disponibili
   */
  isPushNotificationsAvailable(): boolean {
    return (
      this.appService.getConfig().security.pushNotificationActive &&
      Capacitor.isPluginAvailable('PushNotifications')
    );
  }

  /**
   * gestire l'apertura di una notifica
   */
  openNotification(notification: PushNotification): void {}

  /**
   * aggiunge listener di attivazione
   */
  private addRegistrationListener(
    callback: (token: { value: string }) => void
  ): void {
    PushNotifications.addListener('registration', callback);
  }

  /**
   * aggiunge listener su notifica ricevuta
   */
  private addNotificationReceivedListener(
    callback: (notification: { title: string }) => void
  ): void {
    PushNotifications.addListener('pushNotificationReceived', callback);
  }

  /**
   * aggiunge listener su notifica aperta
   */
  private addNotificationActionPerformedListener(
    callback: (notification: { actionId: string; notification: any }) => void
  ): void {
    PushNotifications.addListener('pushNotificationActionPerformed', callback);
  }

  /**
   * controlla e richiedi i permessi se necessario
   */
  private async guaranteePermissions(): Promise<void> {
    let status = await this.checkPermissions();

    if (status.receive === 'prompt') {
      status = await this.requestPermissions();
    }

    if (status.receive !== 'granted') {
      throw new Error('User denied permissions!');
    }
  }

  /**
   * controlla lo stato dei permessi per le notifiche push
   */
  private checkPermissions(): Promise<{ receive: string }> {
    return PushNotifications.checkPermissions();
  }

  /**
   * richiede permesso per notifiche push
   */
  private requestPermissions(): Promise<{ receive: string }> {
    return PushNotifications.requestPermissions();
  }

  /**
   * registra il device
   */
  private register(): Promise<void> {
    return PushNotifications.register();
  }
}
